import { useState } from 'react';

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    restaurant: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d45624qtkakk9n012110', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ nom: '', email: '', telephone: '', restaurant: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-section" className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 sm:opacity-15">
        <div 
          className="w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/eb55d6b49a131102fc4fd2f5447e5254.jpg')`,
            backgroundPosition: 'center 35%',
            backgroundSize: 'cover',
            backgroundAttachment: 'scroll'
          }}
        />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4 sm:mb-6 leading-tight px-4">
            <span className="block">Réservez votre <span className="whitespace-nowrap">rendez-vous</span></span>
            <span className="block bg-gradient-to-r from-pink-300 to-purple-300 bg-clip-text text-transparent">
              gratuit dès aujourd&apos;hui
            </span>
          </h2>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 sm:p-8 md:p-12 border border-white/20">
          <form onSubmit={handleSubmit} data-readdy-form className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label htmlFor="nom" className="block text-white font-accent font-semibold mb-2 text-sm sm:text-base">
                  Nom complet *
                </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition-all duration-300 font-primary text-sm sm:text-base"
                  placeholder="Votre nom et prénom"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-white font-accent font-semibold mb-2 text-sm sm:text-base">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition-all duration-300 font-primary text-sm sm:text-base"
                  placeholder="votre@email.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label htmlFor="telephone" className="block text-white font-accent font-semibold mb-2 text-sm sm:text-base">
                  Téléphone *
                </label>
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition-all duration-300 font-primary text-sm sm:text-base"
                  placeholder="06 12 34 56 78"
                />
              </div>
              <div>
                <label htmlFor="restaurant" className="block text-white font-accent font-semibold mb-2 text-sm sm:text-base">
                  Nom de votre société *
                </label>
                <input
                  type="text"
                  id="restaurant"
                  name="restaurant"
                  value={formData.restaurant}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition-all duration-300 font-primary text-sm sm:text-base"
                  placeholder="Le nom de votre société"
                />
              </div>
            </div>

            <div className="pt-4 sm:pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-heading font-bold text-sm sm:text-base md:text-lg py-4 sm:py-5 px-4 sm:px-6 md:px-8 rounded-xl shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap min-h-[56px] sm:min-h-[64px] md:min-h-[70px]"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2 text-base sm:text-lg md:text-xl"></i>
                    <span className="text-xs sm:text-sm md:text-base">Envoi en cours...</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <span className="text-xs sm:text-sm md:text-base">Augmenter mes ventes maintenant !</span>
                    <i className="ri-rocket-line ml-2 sm:ml-3 text-lg sm:text-xl md:text-2xl"></i>
                  </span>
                )}
              </button>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-500/20 border border-green-400 rounded-xl p-3 sm:p-4 text-center">
                <i className="ri-check-line text-green-400 text-lg sm:text-xl mr-2"></i>
                <span className="text-green-100 font-accent font-semibold text-sm sm:text-base">
                  Merci ! Nous vous contacterons très bientôt.
                </span>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-500/20 border border-red-400 rounded-xl p-3 sm:p-4 text-center">
                <i className="ri-error-warning-line text-red-400 text-lg sm:text-xl mr-2"></i>
                <span className="text-red-100 font-accent font-semibold text-sm sm:text-base">
                  Une erreur s'est produite. Veuillez réessayer.
                </span>
              </div>
            )}
          </form>

          <div className="mt-6 sm:mt-8 text-center">
            <p className="text-purple-200 text-xs sm:text-sm font-primary">
              <i className="ri-shield-check-line mr-2"></i>
              Vos données sont sécurisées et ne seront jamais partagées
            </p>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-8 sm:mt-12 flex flex-row justify-center items-center gap-6 sm:gap-8 text-center">
          <div className="text-white flex flex-col items-center">
            <i className="ri-time-line text-2xl sm:text-3xl text-pink-400 mb-2"></i>
            <div className="font-accent font-semibold text-xs sm:text-sm">Réponse sous 24h</div>
          </div>
          <div className="text-white flex flex-col items-center">
            <i className="ri-phone-line text-2xl sm:text-3xl text-purple-400 mb-2"></i>
            <div className="font-accent font-semibold text-xs sm:text-sm">Audit gratuit</div>
          </div>
          <div className="text-white flex flex-col items-center">
            <i className="ri-shield-check-line text-2xl sm:text-3xl text-pink-400 mb-2"></i>
            <div className="font-accent font-semibold text-xs sm:text-sm">Résultat garantis</div>
          </div>
        </div>
      </div>
    </section>
  );
};
